/**
 * API Instance Configuration
 *
 * This file creates and configures the axios instance for making API calls.
 * It includes interceptors for request/response handling, error management,
 * and authentication token management.
 *
 * Features:
 * - Centralized API configuration
 * - Request/Response interceptors
 * - Automatic token attachment
 * - Error handling and logging
 * - Request/Response transformation
 */

import axios from 'axios';
import { API_CONFIG } from './api-routes';
import { USER_TIMEZONE } from '../utility/Constant';

/**
 * Create axios instance with base configuration
 * This instance will be used throughout the application for API calls
 */
const apiInstance = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        timezone: USER_TIMEZONE,
        Accept: 'application/json',
    },
});

/**
 * Request Interceptor
 *
 * This interceptor runs before every request is sent.
 * It's used to:
 * - Add authentication tokens
 * - Log requests in development
 * - Modify request headers or data
 * - Add common parameters
 */
apiInstance.interceptors.request.use(
    (config) => {
        // Get auth token from localStorage (you can modify this based on your auth strategy)
        const token = localStorage.getItem('apex-saas_token');

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Log request in development
        if (process.env.NODE_ENV === 'development') {
            console.group('🚀 API Request');
            console.log('URL:', config.url);
            console.log('Method:', config.method?.toUpperCase());
            console.log('Headers:', config.headers);
            console.log('Data:', config.data);
            console.groupEnd();
        }

        return config;
    },
    (error) => {
        // Log request error
        if (process.env.NODE_ENV === 'development') {
            console.error('❌ Request Error:', error);
        }
        return Promise.reject(error);
    }
);

/**
 * Response Interceptor
 *
 * This interceptor runs after every response is received.
 * It's used to:
 * - Handle common response patterns
 * - Log responses in development
 * - Handle authentication errors
 * - Transform response data
 */
apiInstance.interceptors.response.use(
    (response) => {
        // Log successful response in development
        if (process.env.NODE_ENV === 'development') {
            console.group('✅ API Response');
            console.log('URL:', response.config.url);
            console.log('Status:', response.status);
            console.log('Data:', response.data);
            console.groupEnd();
        }

        return response;
    },
    (error) => {
        // Log error response in development
        if (process.env.NODE_ENV === 'development') {
            console.group('❌ API Error');
            console.log('URL:', error.config?.url);
            console.log('Status:', error.response?.status);
            console.log('Message:', error.message);
            console.log('Response Data:', error.response?.data);
            console.groupEnd();
        }

        // Handle different error scenarios
        if (error.response) {
            const { status, data } = error.response;

            switch (status) {
                case 401:
                    // Unauthorized - handle token expiration
                    console.warn('🔒 Unauthorized access - redirecting to login');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('token');
                    // You can dispatch a logout action here or redirect to login
                    // window.location.href = '/login';
                    break;

                case 403:
                    // Forbidden - user doesn't have permission
                    console.warn('🚫 Access forbidden');
                    break;

                case 404:
                    // Not found
                    console.warn('🔍 Resource not found');
                    break;

                case 422:
                    // Validation error
                    console.warn('⚠️ Validation error:', data);
                    break;

                case 500:
                    // Server error
                    console.error('🔥 Server error');
                    break;

                default:
                    console.error(`🚨 HTTP Error ${status}:`, data);
            }
        } else if (error.request) {
            // Network error
            console.error('🌐 Network error - no response received');
        } else {
            // Something else happened
            console.error('⚡ Request setup error:', error.message);
        }

        return Promise.reject(error);
    }
);

/**
 * API Helper Functions
 *
 * These functions provide a consistent interface for making API calls
 * and can be used directly or with TanStack Query
 */

/**
 * GET Request Helper
 * @param {string} url - The endpoint URL
 * @param {object} config - Additional axios config
 * @returns {Promise} - Axios response promise
 */
export const apiGet = (url, config = {}) => {
    return apiInstance.get(url, config);
};

/**
 * POST Request Helper
 * @param {string} url - The endpoint URL
 * @param {object} data - Request body data
 * @param {object} config - Additional axios config
 * @returns {Promise} - Axios response promise
 */
export const apiPost = (url, data = {}, config = {}) => {
    return apiInstance.post(url, data, config);
};

/**
 * PUT Request Helper
 * @param {string} url - The endpoint URL
 * @param {object} data - Request body data
 * @param {object} config - Additional axios config
 * @returns {Promise} - Axios response promise
 */
export const apiPut = (url, data = {}, config = {}) => {
    return apiInstance.put(url, data, config);
};

/**
 * PATCH Request Helper
 * @param {string} url - The endpoint URL
 * @param {object} data - Request body data
 * @param {object} config - Additional axios config
 * @returns {Promise} - Axios response promise
 */
export const apiPatch = (url, data = {}, config = {}) => {
    return apiInstance.patch(url, data, config);
};

/**
 * DELETE Request Helper
 * @param {string} url - The endpoint URL
 * @param {object} config - Additional axios config
 * @returns {Promise} - Axios response promise
 */
export const apiDelete = (url, config = {}) => {
    return apiInstance.delete(url, config);
};

/**
 * File Upload Helper
 * @param {string} url - The endpoint URL
 * @param {FormData} formData - Form data containing files
 * @param {object} config - Additional axios config
 * @returns {Promise} - Axios response promise
 */
export const apiUpload = (url, formData, config = {}) => {
    return apiInstance.post(url, formData, {
        ...config,
        headers: {
            ...config.headers,
            'Content-Type': 'multipart/form-data',
        },
    });
};

/**
 * Generic API Call Function
 * This function can be used with TanStack Query for any HTTP method
 *
 * @param {object} options - Request options
 * @param {string} options.method - HTTP method
 * @param {string} options.url - Endpoint URL
 * @param {object} options.data - Request data
 * @param {object} options.params - URL parameters
 * @param {object} options.headers - Additional headers
 * @returns {Promise} - Axios response promise
 */
export const apiCall = ({ method = 'GET', url, data, params, headers, ...config }) => {
    return apiInstance({
        method,
        url,
        data,
        params,
        headers,
        ...config,
    });
};

/**
 * Set Authentication Token
 * Helper function to set the auth token for subsequent requests
 *
 * @param {string} token - The authentication token
 */
export const setAuthToken = (token) => {
    if (token) {
        localStorage.setItem('authToken', token);
        apiInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        localStorage.removeItem('authToken');
        delete apiInstance.defaults.headers.common['Authorization'];
    }
};

/**
 * Clear Authentication Token
 * Helper function to clear the auth token
 */
export const clearAuthToken = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    delete apiInstance.defaults.headers.common['Authorization'];
};

/**
 * Get Current Auth Token
 * @returns {string|null} - Current auth token
 */
export const getAuthToken = () => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
};

// Export the configured axios instance as default
export default apiInstance;
